import { createContext, useContext, useEffect, useRef, useState } from 'react';

interface User {
  id: string;
  email: string;
  role: 'admin' | 'superadmin';
}

interface AuthContextValue {
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const API_URL     = import.meta.env.VITE_API_URL ?? '';
const STORAGE_KEY = 'tsuyosa_refresh_token';
const REFRESH_MS  = 50 * 60 * 1000;

function parseUserFromToken(token: string): User | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
    const role = payload.role;
    if (!payload.sub || !payload.email || !role) return null;
    return { id: payload.sub, email: payload.email, role };
  } catch { return null; }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser]               = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading]     = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function clearTimer() {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }

  function scheduleRefresh(token: string) {
    clearTimer();
    timerRef.current = setTimeout(() => silentRefresh(token), REFRESH_MS);
  }

  async function silentRefresh(refreshToken: string) {
    try {
      const res = await fetch(`${API_URL}/api/v1/admin/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) throw new Error('Refresh failed');
      const data = await res.json();
      const parsed = parseUserFromToken(data.data.access_token);
      if (!parsed) throw new Error('Invalid token payload');
      setUser(parsed);
      setAccessToken(data.data.access_token);
      localStorage.setItem(STORAGE_KEY, data.data.refresh_token);
      scheduleRefresh(data.data.refresh_token);
    } catch { handleLogoutCleanup(); }
  }

  function handleLogoutCleanup() {
    clearTimer();
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem(STORAGE_KEY);
  }

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) { setIsLoading(false); return; }
    silentRefresh(stored).finally(() => setIsLoading(false));
    return () => clearTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login(email: string, password: string) {
    const res = await fetch(`${API_URL}/api/v1/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error?.message ?? 'Login failed');
    setUser(data.data.user);
    setAccessToken(data.data.access_token);
    localStorage.setItem(STORAGE_KEY, data.data.refresh_token);
    scheduleRefresh(data.data.refresh_token);
  }

  async function logout() {
    if (accessToken) {
      await fetch(`${API_URL}/api/v1/admin/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      }).catch(() => {});
    }
    handleLogoutCleanup();
  }

  return (
    <AuthContext.Provider value={{ user, accessToken, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
